import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

async function generateLoanAmountExcel(apiData, startDate, endDate) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Loan Amount Report");

  const slabs = [
    { key: "Below100K", label: "≤100K" },
    { key: "100KTo250K", label: "100K–250K" },
    { key: "250KTo500K", label: "250K–500K" },
    { key: "500KTo1M", label: "500K–1M" },
    { key: "Above1M", label: ">1M" }
  ];

  // Calculate total columns to merge the top header across the whole width
  const totalColumns = 1 + (apiData.length * slabs.length * 2);

  // -------------------------------
  // 1. Top Headers (Title, Start Date, End Date)
  // -------------------------------
  
  // Row 1: Main Title
  worksheet.mergeCells(1, 1, 1, 1);
  const titleCell = worksheet.getCell("A1");
  titleCell.value = "LOAN AMOUNT REPORT";
  titleCell.font = { bold: true, size: 14, color: { argb: "FFFFFF" } };
  titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } };
  titleCell.alignment = { horizontal: "center" };

const formatDateSL = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};


// Row 2: Start Date
worksheet.getCell("A2").value = `Start Date: ${formatDateSL(startDate)}`;
worksheet.getCell("A2").font = { italic: true };

// Row 3: End Date
worksheet.getCell("A3").value = `End Date: ${formatDateSL(endDate)}`;
worksheet.getCell("A3").font = { italic: true };


  
  // Header Style Constants
  const headerFill = { type: "pattern", pattern: "solid", fgColor: { argb: "D9E1F2" } };
  const subHeaderFill = { type: "pattern", pattern: "solid", fgColor: { argb: "EAEAEA" } };

  // Loan Type column (Merged across 3 rows: 4, 5, and 6)
  worksheet.mergeCells(4, 1, 6, 1);
  const loanTypeHeader = worksheet.getCell("A4");
  loanTypeHeader.value = "Loan Type";
  loanTypeHeader.font = { bold: true };
  loanTypeHeader.fill = headerFill;
  loanTypeHeader.alignment = { vertical: "middle", horizontal: "center" };

  let colIndex = 2;

  apiData.forEach(branch => {
    const branchStartCol = colIndex;
    const branchColWidth = slabs.length * 2;

    // Branch Name Header (Row 4)
    worksheet.mergeCells(4, branchStartCol, 4, branchStartCol + branchColWidth - 1);
    const branchCell = worksheet.getCell(4, branchStartCol);
    branchCell.value = branch.branchName;
    branchCell.font = { bold: true };
    branchCell.fill = headerFill;
    branchCell.alignment = { horizontal: "center" };

    slabs.forEach(slab => {
      // Slab Label Header (Row 5)
      worksheet.mergeCells(5, colIndex, 5, colIndex + 1);
      const slabCell = worksheet.getCell(5, colIndex);
      slabCell.value = slab.label;
      slabCell.font = { bold: true };
      slabCell.fill = subHeaderFill;
      slabCell.alignment = { horizontal: "center" };

      // Count/Amount Headers (Row 6)
      const countCell = worksheet.getCell(6, colIndex);
      const amountCell = worksheet.getCell(6, colIndex + 1);
      
      countCell.value = "Count";
      amountCell.value = "Amount";
      
      countCell.font = { bold: true, size: 10 };
      amountCell.font = { bold: true, size: 10 };
      
      countCell.fill = subHeaderFill;
      amountCell.fill = subHeaderFill;

      colIndex += 2;
    });
  });

  // -------------------------------
  // 3. Data Rows (Starts at Row 7)
  // -------------------------------
  const loanTypes = new Set();
  apiData.forEach(branch => {
    branch.loanAmountWiseDtoList.forEach(loan => {
      loanTypes.add(loan.productName);
    });
  });

  loanTypes.forEach(productName => {
    const rowData = [productName];

    apiData.forEach(branch => {
      const loan = branch.loanAmountWiseDtoList.find(l => l.productName === productName);
      slabs.forEach(slab => {
        rowData.push(loan ? loan[`count${slab.key}`] : 0);
        rowData.push(loan ? loan[`sum${slab.key}`] : 0);
      });
    });

    const row = worksheet.addRow(rowData);
    
    // Currency format for amount columns (every even index starting from column 3)
    row.eachCell((cell, colNumber) => {
      if (colNumber > 1 && colNumber % 2 === 1) {
        cell.numFmt = "#,##0.00";
      }
    });
  });

  // -------------------------------
// 3b. Grand Total Row using Formula
// -------------------------------

  const dataStartRow = 7; // First row of loan data
  const dataEndRow = dataStartRow + loanTypes.size - 1;



const totalRow = worksheet.addRow(["Grand Total"]);

// Loop over all columns except the first (Loan Type)
for (let col = 2; col <= worksheet.columnCount; col++) {
  // Formula: SUM from row 7 to last data row
  const startRow = dataStartRow;
  const endRow = dataStartRow + loanTypes.size - 1;
  totalRow.getCell(col).value = { formula: `SUM(${worksheet.getColumn(col).letter}${startRow}:${worksheet.getColumn(col).letter}${endRow})` };
}

// Apply bold font and background color
totalRow.font = { bold: true };
totalRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD966" } };

// Apply number format for amount columns (every even index starting from column 3)
totalRow.eachCell((cell, colNumber) => {
  if (colNumber > 1 && colNumber % 2 === 1) {
    cell.numFmt = "#,##0.00";
  }
  cell.alignment = { vertical: "middle", horizontal: "center" };
});


  // -------------------------------
  // 4. Styling & Layout
  // -------------------------------
  worksheet.getColumn(1).width = 50;
  worksheet.columns.forEach((col, idx) => {
    if (idx > 0) col.width = 15;
  });
worksheet.eachRow((row, rowNumber) => {
  
  // Apply grey background to every second data row (Starting from row 7)
  // rowNumber % 2 === 0 will color even rows. 
  // Change to !== 0 if you want to start the grey on the first data row.
  if (rowNumber >= 7 && rowNumber <= dataEndRow) {
    if (rowNumber % 2 === 0) {
      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F2F2F2" } // Very light grey
      };
    }
  }

  row.eachCell((cell) => {
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" }
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
  });
});

worksheet.views = [{ state: "frozen", xSplit: 1, ySplit: 6 }];


  // -------------------------------
  // 5. Save File
  // -------------------------------
  const fileName = "LoanAmountReport";
  const buffer = await workbook.xlsx.writeBuffer();
  const now = new Date();

const pad = (n) => String(n).padStart(2, "0");

const finalFileName = `${fileName}_${
  now.getFullYear()
}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${
  pad(now.getHours())
}${pad(now.getMinutes())}${pad(now.getSeconds())
}.xlsx`;

  saveAs(new Blob([buffer]), finalFileName);
}

export default generateLoanAmountExcel;